import { PlatformIcon } from '../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { chartMarkGeometry } from '../ChartFrame/chartTheme';
import styles from './ChartLegend.module.css';

/**
 * Figma: `_Legend` (421:47686) — `Orientation = Vertical | Horizontal | Platforms`.
 *
 * Fifteen `Legend series` rows, each an 8x8 `radius/xxs` swatch bound to
 * `chart/1` … `chart/15` in order, `space/6` from its label in `chart/legend`.
 * Rows sit `space/12` apart in both orientations.
 *
 * Rendered as DOM outside the Recharts tree rather than through Recharts'
 * `<Legend>`. Figma draws it as a standalone component with its own orientation
 * axis, and the `Card` gives it its own `Legend` slot below the chart — so it is
 * a sibling of the plot in the design, and it is one here too. That also keeps
 * its type and spacing in CSS where stylelint can check them.
 *
 * `Orientation=Platforms` (893:32882) is the 2026-09-16 addition: where the
 * other two orientations set a swatch beside a text label, this one sets it
 * beside the channel's own brand mark and drops the text entirely — seven
 * `Legend series` frames, each a 6x6 swatch and a 12x12 logo `space/6` apart,
 * the frames 16 apart on one row.
 *
 * Dropping the text is the file's call, not a shortcut here, and it costs the
 * legend its accessible text — so the mark carries the channel name as its
 * `aria-label` instead of being decorative. That is the one thing this variant
 * adds that Figma cannot draw.
 *
 * Not interactive by default: Figma draws no hover or selected state for a
 * legend entry, so clicking one does not toggle its series. See plan.md §7.
 * `onToggle` opts in (the executive view's brand charts, GP 2026-11-03): each
 * entry is then a pressed-or-not button, and an entry whose series is hidden
 * (`off`) reads struck through, its swatch faded.
 */
export type ChartLegendItem = {
  /** Figma `Series n` — the label. */
  label: string;
  /**
   * The swatch colour, as returned by `seriesColor()` — a custom-property
   * reference on the `chart/n` ramp. Not a raw colour by convention: charts
   * resolve their palette through `chartTheme`.
   */
  color: string;
  /**
   * Draw the swatch at `chartMarkGeometry.mutedOpacity`, to key a mark drawn
   * faded (`BarChart.mutedCells`). Not in Figma, which has no faded swatch. It
   * gives the key the same two strengths the plot uses.
   */
  muted?: boolean;
  /** With `onToggle`: the series is hidden, and clicking the entry shows it again. */
  off?: boolean;
};

export type ChartLegendProps = {
  items: ChartLegendItem[];
  /** Figma `Orientation`. */
  orientation?: 'horizontal' | 'vertical' | 'platforms';
  className?: string;
  /** Make each entry a button that shows or hides its series, by index. Not in Figma. */
  onToggle?: (index: number) => void;
};

export function ChartLegend({
  items,
  orientation = 'horizontal',
  className,
  onToggle,
}: ChartLegendProps) {
  const platforms = orientation === 'platforms';

  return (
    <ul className={[styles.legend, styles[orientation], className].filter(Boolean).join(' ')}>
      {items.map((item, i) => {
        /*
         * Undefined for a series that is not a channel, or a channel with no
         * mark in the file. Either way the row falls back to the labelled form
         * rather than showing a swatch with nothing beside it — a legend that
         * cannot name its series says nothing at all.
         */
        const platform = platforms ? platformFor(item.label) : undefined;

        const entry = (
          <>
            {/*
             * The swatch carries meaning only in combination with what follows
             * it — so it is decorative to a screen reader and the label, or the
             * mark's own `aria-label`, is the accessible text. Not in Figma.
             */}
            <span
              className={styles.swatch}
              style={{
                background: item.color,
                opacity: item.muted || item.off ? chartMarkGeometry.mutedOpacity : undefined,
              }}
              aria-hidden="true"
            />
            {platform ? (
              <PlatformIcon name={platform.name} label={item.label} />
            ) : (
              <span className={styles.label}>{item.label}</span>
            )}
          </>
        );

        return (
          <li key={item.label} className={styles.item}>
            {onToggle ? (
              <button
                type="button"
                className={[styles.item, styles.toggle, item.off && styles.off]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={!item.off}
                title={item.off ? `Show ${item.label}` : `Hide ${item.label}`}
                onClick={() => onToggle(i)}
              >
                {entry}
              </button>
            ) : (
              entry
            )}
          </li>
        );
      })}
    </ul>
  );
}
